import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Badge, createStyles, Text, Tooltip} from '@mantine/core';
import React from 'react';

import VersionHistoryItemBase, {
  VersionHistoryItemAvatar,
} from 'notebook/version-history/VersionHistoryItemBase';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

export const FILE_VERSION_HISTORY_PANEL_WIDTH_PX = 320;

export interface FileVersionHistoryEntry {
  id: string;
  versionNumber: number;
  createdAt: string;
  description: string | null;
  creator: {id: string; name: string} | null;
}

interface FileVersionHistoryPanelProps {
  className?: string;
  currentVersionId: string | null;
  /** Called with a version to revert to; omitted when the viewer can't revert. */
  onRevert?: (versionId: string) => void;
  onClose: () => void;
  onDownload: (versionId: string) => void;
  onSelectVersion: (versionId: string) => void;
  selectedVersionId: string;
  /** Versions to list, newest first. */
  versions: Array<FileVersionHistoryEntry>;
}

const useStyles = createStyles((theme) => ({
  root: {
    backgroundColor: theme.white,
    borderLeft: `1px solid ${theme.colors.gray[2]}`,
    display: 'flex',
    flexDirection: 'column',
    width: FILE_VERSION_HISTORY_PANEL_WIDTH_PX,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: `${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.md}`,
  },
  title: {
    color: theme.colors.gray[9],
    fontSize: theme.fontSizes.lg,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  versionsList: {
    flex: 1,
    overflowY: 'auto',
  },
  versionTitle: {
    alignItems: 'center',
    color: theme.colors.gray[9],
    display: 'flex',
    fontSize: theme.fontSizes.md,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    gap: theme.spacing.xs,
  },
  timestamp: {
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.sm,
  },
  description: {
    color: theme.colors.gray[9],
    fontSize: theme.fontSizes.sm,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
  },
}));

/** Side panel listing a file's published versions, with who published each one, when, and why. */
export default function FileVersionHistoryPanel({
  className,
  currentVersionId,
  onClose,
  onDownload,
  onRevert,
  onSelectVersion,
  selectedVersionId,
  versions,
}: FileVersionHistoryPanelProps): JSX.Element {
  const {classes, cx} = useStyles();
  return (
    <aside
      aria-label="Version history"
      className={cx(classes.root, className)}
      {...getTestProps({component: 'FileVersionHistoryPanel', element: 'root'})}
    >
      <div className={classes.header}>
        <Text className={classes.title}>Version history</Text>
        <Tooltip label="Close" position="left">
          <ActionIcon aria-label="Close version history" onClick={onClose} size="sm" variant="subtle">
            <BenchlingIcon icon="xmark" />
          </ActionIcon>
        </Tooltip>
      </div>
      <div className={classes.versionsList}>
        {versions.map((version) => {
          const isCurrent = version.id === currentVersionId;
          return (
            <VersionHistoryItemBase
              key={version.id}
              dataTestAttrs={{
                component: 'FileVersionHistoryPanel',
                element: 'version',
                key: version.id,
              }}
              isSelected={version.id === selectedVersionId}
              onClick={() => {
                onSelectVersion(version.id);
              }}
              firstOption={{
                text: 'Download version',
                icon: 'download',
                onClick: () => {
                  onDownload(version.id);
                },
              }}
              secondOption={{
                text: 'Revert to this version',
                icon: 'undo',
                onClick:
                  onRevert != null && !isCurrent
                    ? () => {
                        onRevert(version.id);
                      }
                    : undefined,
              }}
              rows={[
                <div key="title" className={classes.versionTitle}>
                  <span>Version {version.versionNumber}</span>
                  {isCurrent && (
                    <Badge color="blue" size="xs" variant="light">
                      Current
                    </Badge>
                  )}
                </div>,
                <span key="timestamp" className={classes.timestamp}>
                  {readableTime(version.createdAt)}
                </span>,
                ...(version.creator != null
                  ? [<VersionHistoryItemAvatar key="creator" user={version.creator} />]
                  : []),
                ...(version.description != null
                  ? [
                      <Text key="description" className={classes.description}>
                        {version.description}
                      </Text>,
                    ]
                  : []),
              ]}
            />
          );
        })}
      </div>
    </aside>
  );
}
